import { Button, Grid, Icon, Spinner, Typography } from 'gu-ui-kit'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import useFiltersManager from 'utils/hooks/useFiltersManager'
import config from 'modules/Referral/ReferralClients/filters'
import FilterPanel from 'components/Filters/FilterPanel'
import { useReferralClients } from 'modules/Referral/ReferralClients/hooks/useReferralClients'
import clsx from 'clsx'
import GridSpinner from 'components/UI/GridSpinner'
import styles from 'modules/Referral/ReferralClients/referral-clients.module.scss'
import { useReferralLinks } from 'modules/Referral/ReferralLinks/hooks/useReferralLinks'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'
import { CLIENTS_SET_FILTERS } from 'modules/Referral/ReferralClients/types'
import { Table } from 'modules/Referral/ReferralClients/subcomponents'
import isEmpty from 'lodash/isEmpty'
import EmptyResults from 'components/EmptyResults'
import { ReactComponent as EmptyResultsSVG } from 'images/illustration/no-referrals.svg'
import { Link } from 'react-router-dom'
import useGeneratePathI18n from 'utils/hooks/useGeneratePathI18n'
import { ROUTES } from 'constants/routes'
import PageHeader from 'components/PageHeader'

const ReferralClients = () => {
  const { t } = useTranslation()
  const [page, setPage] = useState(1)
  const [firstLoad, setFirstLoad] = useState(true)
  const [clientsCreated, setClientsCreated] = useState(false)

  const breakpoints = useBreakpoint()

  const { fetchClients, updateClient, resetClients } = useReferralClients()

  const { fetchLinks, resetLinks } = useReferralLinks()
  const pathGen = useGeneratePathI18n()

  const {
    filters,
    data: clients,
    pagination,
  } = useSelector((state) => state['referral/clients'])

  const filterManager = useFiltersManager({
    filters: filters,
    config,
    setAction: CLIENTS_SET_FILTERS,
    localStorageKey: 'referral-clients',
  })

  useEffect(() => {
    setPage(1)
  }, [filterManager.params])

  useEffect(() => {
    if (!filterManager.params) {
      return
    }

    fetchClients({ page })
  }, [filterManager.params, page])

  const handleChangeName = useCallback(
    (id, name) => updateClient({ id, data: { name } }),
    [],
  )

  useEffect(() => {
    fetchLinks({ offset: 0, limit: 50 }).then(({ data = [] }) => {
      filterManager.updateConfiguration({
        key: 'referral_link_ids',
        val: {
          options: data.map(({ name, id }) => ({
            value: id,
            label: name,
          })),
        },
      })
    })
    return () => {
      resetClients()
      resetLinks()
    }
  }, [])

  useEffect(
    () =>
      firstLoad &&
      setClientsCreated(
        (isEmpty(filterManager.params) &&
          fetchClients.finished &&
          pagination?.total > 0) ||
          !isEmpty(filterManager.params),
      ),
    [filterManager.params, fetchClients, pagination],
  )

  useEffect(
    () => fetchClients.finished && setFirstLoad(false),
    [fetchClients.finished, pagination],
  )

  return (
    <Grid direction={'column'} className={styles['container']}>
      <PageHeader
        color={'Label-Primary'}
        className={breakpoints.fbs ? 'margin-bottom-8' : 'margin-bottom-16'}
      >
        {t('referral::referral_clients.heading')}
      </PageHeader>
      <Typography
        variant={'Body-20'}
        color={'Label-Secondary'}
        component={'p'}
        className={'margin-bottom-24'}
        noMargin
      >
        {t('referral::referral_clients.summary')}
      </Typography>

      {firstLoad && fetchLinks.progress && <GridSpinner width={32} />}

      {!clientsCreated && !firstLoad && (
        <EmptyResults
          header={t('referral::referral_clients.empty_list.heading')}
          text={
            <Grid alignItems={'center'} direction={'column'} gap={32}>
              <Typography variant={'Body-25'} color={'Label-Fifth'}>
                {t('referral::referral_clients.empty_list.summary')}
              </Typography>
              <Button
                fullWidth={breakpoints.fbs}
                variant={'primary'}
                component={Link}
                to={pathGen(ROUTES.REFERRAL_LINKS_CREATE)}
                size="l"
                startIcon={<Icon name={'plus'} />}
              >
                {t('referral::referral_links.create_link')}
              </Button>
            </Grid>
          }
          image={<EmptyResultsSVG />}
        />
      )}
      {clientsCreated && !firstLoad && (
        <>
          <Grid className={'margin-bottom-32'} cols={1}>
            {filterManager.filters && (
              <FilterPanel
                filterManager={filterManager}
                className={styles['filter-panel']}
              />
            )}
          </Grid>
          {fetchClients.progress && (
            <GridSpinner width={32} style={{ height: '100%' }} />
          )}
          {clients && (
            <Table handleChangeName={handleChangeName} data={clients} />
          )}
          {clients?.length > 0 && pagination?.total !== clients?.length && (
            <Grid
              className={'margin-top-40 margin-bottom-20'}
              alignItems={'center'}
              justifyContent={'center'}
            >
              {pagination.total !== clients.length && (
                <Button
                  onClick={() => setPage(page + 1)}
                  className={clsx(styles['show-more__button'])}
                  variant={'link'}
                  startIcon={
                    fetchClients.progress ? (
                      <Spinner width={16} color={'inherit'} />
                    ) : null
                  }
                  endIcon={
                    <Icon name={'arrow1-down'} color={'inherit'} size={'s'} />
                  }
                >
                  {t('common::pagination.show_more')}
                </Button>
              )}
            </Grid>
          )}
        </>
      )}
    </Grid>
  )
}

export default ReferralClients
